import React, { useState, useEffect } from 'react';
import { auth } from '../../../../../firebase/config';
import Modal from '../../../../../components/Modal';
import TextField from '../../../../../components/TextField';
import Button from '../../../../../components/Button';
import { FiUser, FiPhone, FiMail, FiBriefcase, FiLock } from 'react-icons/fi';
import Icon from '../../../../../components/Icon';
import { PSO_DESIGNATION_OPTIONS, submitPSO, type PSOFormData } from '../service/addStaffService';

type AddStaffModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    bankId?: string;
};

export default function AddStaffModal({ isOpen, onClose, onSuccess, bankId }: AddStaffModalProps) {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<PSOFormData>({
        name: '',
        contact: '',
        email: '',
        designation: '',
        password: '',
        confirmPassword: '',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (!isOpen) {
            // Reset form when modal closes
            setFormData({
                name: '',
                contact: '',
                email: '',
                designation: '',
                password: '',
                confirmPassword: '',
            });
            setErrors({});
        }
    }, [isOpen]);

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        if (!formData.contact.trim()) {
            newErrors.contact = 'Contact number is required';
        } else if (!/^[0-9+\-\s()]+$/.test(formData.contact.trim())) {
            newErrors.contact = 'Please enter a valid contact number';
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.designation.trim()) {
            newErrors.designation = 'Designation is required';
        }

        // Always create Firebase Auth login for staff members
        if (!formData.password || !formData.password.trim()) {
            newErrors.password = 'Password is required';
        } else if (formData.password.trim().length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }
        if (!formData.confirmPassword || !formData.confirmPassword.trim()) {
            newErrors.confirmPassword = 'Confirm password is required';
        } else if (formData.password?.trim() !== formData.confirmPassword.trim()) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        const uid = auth.currentUser?.uid;
        if (!uid || !bankId) {
            setErrors({ submit: 'User not authenticated or bank ID missing' });
            return;
        }

        setLoading(true);
        try {
            await submitPSO(bankId, formData);

            // Reset form
            setFormData({
                name: '',
                contact: '',
                email: '',
                designation: '',
                password: '',
                confirmPassword: '',
            });
            setErrors({});

            onSuccess();
        } catch (err: any) {
            console.error('Error adding staff:', err);
            setErrors({ submit: err.message || 'Failed to add staff. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            name: '',
            contact: '',
            email: '',
            designation: '',
            password: '',
            confirmPassword: '',
        });
        setErrors({});
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Add New Staff"
            size="lg"
            closeOnBackdropClick={false}
        >
            <form onSubmit={handleSubmit} className="p-6">
                <div className="space-y-4">
                    <TextField
                        label="Name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Enter staff name"
                        required
                        prefix={<Icon icon={FiUser} className="text-gray-400" />}
                        className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && (
                        <p className="-mt-3 text-xs text-red-600 dark:text-red-400">{errors.name}</p>
                    )}

                    <TextField
                        label="Contact"
                        type="tel"
                        value={formData.contact}
                        onChange={(e) => handleInputChange('contact', e.target.value)}
                        placeholder="Enter contact number"
                        required
                        prefix={<Icon icon={FiPhone} className="text-gray-400" />}
                        className={errors.contact ? 'border-red-500' : ''}
                    />
                    {errors.contact && (
                        <p className="-mt-3 text-xs text-red-600 dark:text-red-400">{errors.contact}</p>
                    )}

                    <TextField
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Enter email address"
                        required
                        prefix={<Icon icon={FiMail} className="text-gray-400" />}
                        className={errors.email ? 'border-red-500' : ''}
                    />
                    {errors.email && (
                        <p className="-mt-3 text-xs text-red-600 dark:text-red-400">{errors.email}</p>
                    )}

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Designation <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Icon
                                icon={FiBriefcase}
                                className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                            />
                            <select
                                value={formData.designation}
                                onChange={(e) => {
                                    handleInputChange('designation', e.target.value);
                                }}
                                className={`w-full rounded-xl border py-3 pl-10 pr-4 text-sm outline-none transition bg-white text-gray-900 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 ${errors.designation ? 'border-red-500' : ''}`}
                                required
                            >
                                <option value="">Select Designation</option>
                                {PSO_DESIGNATION_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {errors.designation && (
                            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.designation}</p>
                        )}
                    </div>

                    <TextField
                        label="Password"
                        type="password"
                        value={formData.password || ''}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder="Enter password (min 6 characters)"
                        required
                        prefix={<Icon icon={FiLock} className="text-gray-400" />}
                        className={errors.password ? 'border-red-500' : ''}
                    />
                    {errors.password && (
                        <p className="-mt-3 text-xs text-red-600 dark:text-red-400">{errors.password}</p>
                    )}

                    <TextField
                        label="Confirm Password"
                        type="password"
                        value={formData.confirmPassword || ''}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        placeholder="Confirm password"
                        required
                        prefix={<Icon icon={FiLock} className="text-gray-400" />}
                        className={errors.confirmPassword ? 'border-red-500' : ''}
                    />
                    {errors.confirmPassword && (
                        <p className="-mt-3 text-xs text-red-600 dark:text-red-400">{errors.confirmPassword}</p>
                    )}

                    <p className="-mt-3 text-xs text-gray-500 dark:text-gray-400">
                        This will create a Firebase Auth login for the staff member.
                    </p>

                    {errors.submit && (
                        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
                            {errors.submit}
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading ? 'Adding...' : 'Add Staff'}
                        </Button>
                    </div>
                </div>
            </form>
        </Modal>
    );
}

